"use client"

import React, {useEffect} from "react";
import Script from "next/script";
import {ParsedUrlQuery} from "querystring";
import {orderTransaction, transaction} from "./gtm";
import {
    DEFAULT_GTM_ID,
    PARAMETER_CAMPAIGN_ID,
    PARAMETER_CHANNEL_ID,
    PARAMETER_ORDER_TRANSACTION_ID,
    PARAMETER_TRANSACTION_ID,
    SESSION_KEY_ORDER_TRANSACTION_DATA,
    SESSION_KEY_TRANSACTION_DATA,
    PARAMETER_DONATION_SUBMIT_PENDING,
    PARAMETER_FORMDATA
} from "../../pages/config/settings";
import {useDonationSubmitTracking} from "tracking/hooks/useDonationSubmitTracking";
import {useSessionContext} from "../../context/SessionContext";
import {usePageContext} from "../../context/PageContext";
import {CartForTransaction} from "../../components/forms/F005OrderForm/formUtils";
import useOverlayService from "../../utility/consentManagement/useOverlayService";
import useHandleScripts from "../../utility/consentManagement/useHandleScripts";
import {useRouter} from "next/router";
import {
    resetScrollTrackingForPage,
    startScrollTracking,
    stopScrollTracking
} from "tracking/events/scroll";
import {dataLayerPageView} from "tracking/events/page";

interface Props {
    gtmId?: string,
    referrer?: string | URL | null,
}

const DIRECT_ACCESS = "direct";
const OTHER_LINKS = "referal";

function getGtmId(gtmId?: string): string {
    if (gtmId) {
        return gtmId;
    } else {
        return DEFAULT_GTM_ID;
    }
}

function getEnvironmentLabel(): string {
    return process.env.NODE_ENV === "production"
        ? "Production"
        : "Development";
}

const getMatchingParam = (queryParams?: ParsedUrlQuery, paramSettings?: any) => {
    if (!queryParams || !paramSettings) return;
    for (let i = 0; i < Object.keys(queryParams).length; i++) {
        const paramValue = queryParams[Object.keys(queryParams)[i]];
        if (typeof paramValue !== "string") {
            break;
        }
        let mappedParamValue = paramSettings?.[Object.keys(queryParams)[i]];

        if (typeof mappedParamValue !== "string") {
            mappedParamValue = paramSettings?.[Object.keys(queryParams)[i]]?.[paramValue];
        }
        if (mappedParamValue) return mappedParamValue;
    }
}
const getDomain = (url: string | URL): string => {
    const parsedURL = new URL(url);
    return parsedURL.origin;
}

function buildInitialDataLayerScript(): string {
    const environmentLabel = getEnvironmentLabel();

    return `window.dataLayer = window.dataLayer || [];
window.__INITIAL_PAGE_LOCATION__ = window.location.href;
window.dataLayer.push({
  event: "page_meta",
  original_location: window.__INITIAL_PAGE_LOCATION__,
  page_environment: "${environmentLabel}",
  page_location: window.location.href,
  page_name: window.location.pathname,
  page_title: document.title,
  event_source: "source_code"
});
function gtag() { dataLayer.push(arguments); }
gtag("consent", "default", { ad_user_data: "denied", ad_personalization: "denied", ad_storage: "denied", analytics_storage: "denied", wait_for_update: 2000});
gtag("set", "ads_data_redaction", true);`;
}

export default function Analytics(props: Props) {
    const {getFromSession, addToSession, removeFromSession, getJsonFromSession} = useSessionContext();
    const GTM_ID = getGtmId(props.gtmId);
    const [transactionId, setTransactionId] = React.useState('init');
    const pageContext = usePageContext();

    const router = useRouter();
    const pathname = router.pathname;
    const searchParams = router.query;
    const queryParams = router.query;

    const marketingSettings = pageContext?.page?.marketingSettings;
    const channelDocRefererSettings = marketingSettings?.["channel-doc-referer"];
    const channelUrlParameterSettings = marketingSettings?.["channel-url-parameter"];
    const channelIdSettings = marketingSettings?.["channelIDs"];
    const referrerDomain = props.referrer ? getDomain(props.referrer) : undefined;

    const initialDataLayerScript = buildInitialDataLayerScript();

    const gtmScript = `(function(w, d, s, l, i) { w[l] = w[l] || []; w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    var f = d.getElementsByTagName(s)[0], j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : ''; j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode.insertBefore(j, f); })(window, document, 'script', 'dataLayer', '${GTM_ID}');`

    useOverlayService();
    useHandleScripts();

    // Deferred donation_submit listener: check session and push success on next page load.
    const {onSubmitSuccess} = useDonationSubmitTracking();
    useEffect(() => {
        try {
            if (typeof window === 'undefined') return;
            const pending = window.sessionStorage.getItem(PARAMETER_DONATION_SUBMIT_PENDING);
            const donationId = window.sessionStorage.getItem(PARAMETER_TRANSACTION_ID) || undefined;
            const formDataRaw = window.sessionStorage.getItem(PARAMETER_FORMDATA);
            if (pending === '1' && donationId && formDataRaw) {
                const formData = JSON.parse(formDataRaw || '{}');
                onSubmitSuccess(formData, String(donationId));
                window.sessionStorage.removeItem(PARAMETER_DONATION_SUBMIT_PENDING);
            }
        } catch {
            // noop
        }
    }, [onSubmitSuccess]);

    useEffect(() => {
        const campaignId = getFromSession(PARAMETER_CAMPAIGN_ID);
        let channelId = getFromSession(PARAMETER_CHANNEL_ID);

        if (!channelId) {
            let marketingParam;
            if (getMatchingParam(queryParams, channelUrlParameterSettings)) {
                marketingParam = getMatchingParam(queryParams, channelUrlParameterSettings);
            } else if (!referrerDomain) {
                marketingParam = DIRECT_ACCESS;
            } else {
                marketingParam = channelDocRefererSettings?.hasOwnProperty(referrerDomain) ?
                    channelDocRefererSettings[referrerDomain] : OTHER_LINKS;
            }
            channelId = channelIdSettings?.[marketingParam];
        }

        if (channelId) {
            addToSession(PARAMETER_CHANNEL_ID, channelId);
        }

        const tId = getFromSession(PARAMETER_TRANSACTION_ID);
        const otId = getFromSession(PARAMETER_ORDER_TRANSACTION_ID);

        if (tId && tId !== '' && tId != transactionId) {
            setTransactionId(tId);
            const transactionData = getJsonFromSession(SESSION_KEY_TRANSACTION_DATA);
            transaction(tId, campaignId, channelId, transactionData);
            removeFromSession(PARAMETER_TRANSACTION_ID);
        } else if (otId && otId !== '' && otId != transactionId) {
            setTransactionId(otId);
            const orderTransactionData = getJsonFromSession(
                SESSION_KEY_ORDER_TRANSACTION_DATA) as CartForTransaction | null;
            orderTransaction(otId, orderTransactionData);
            removeFromSession(PARAMETER_ORDER_TRANSACTION_ID);
            removeFromSession(SESSION_KEY_ORDER_TRANSACTION_DATA);
        }
    }, [
                  pathname,
                  searchParams,
                  addToSession,
                  removeFromSession,
                  getFromSession,
                  getJsonFromSession,
                  queryParams,
                  channelUrlParameterSettings,
                  referrerDomain,
                  channelDocRefererSettings,
                  channelIdSettings,
                  transactionId
              ])

    useEffect(() => {
        startScrollTracking();

        return () => {
            stopScrollTracking();
        };
    }, []);

    useEffect(() => {
        if (!router.asPath) return;
        const campaignId = getFromSession(PARAMETER_CAMPAIGN_ID) as string | null;
        const channelId = getFromSession(PARAMETER_CHANNEL_ID) as string | null;
        dataLayerPageView(router.asPath, campaignId ?? null, channelId ?? null);
        resetScrollTrackingForPage();
    }, [router.asPath, getFromSession]);

    return (
        <>
            <noscript>
                <iframe
                    src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
                    height="0"
                    width="0"
                    style={{display: "none", visibility: "hidden"}}
                />
            </noscript>
            {/* eslint-disable @next/next/no-before-interactive-script-outside-document -- Must run before hydration and depend on runtime data; revisit for App Router. */}
            <Script
                id="datalayer-script"
                strategy="beforeInteractive"
                dangerouslySetInnerHTML={{
                    __html: initialDataLayerScript
                }}
            />
            <Script
                id="gtm-script"
                strategy="beforeInteractive"
                type="text/plain"
                data-usercentrics="Google Tag Manager"
                dangerouslySetInnerHTML={{
                    __html: gtmScript
                }}
            />
        </>
    )
}